import { Dispatch, SetStateAction, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { getRideDetails } from '@rideiq/api/services/Rides';
import { useTimezone } from '@rideiq/hooks/useTimezone';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { addMinutes } from 'date-fns';

export const RIDE_QUERY_KEY = 'ride';

export const useRideQuery = ({ refetchOnMount }: { refetchOnMount?: boolean } = {}) => {
  const { rideId } = useParams() as { rideId: string };
  const queryClient = useQueryClient();
  const { getDateInTimezone } = useTimezone();
  const cacheKey = rideId || window.location.pathname.replace(/\/riders\/new\/?$/, '');

  const serializeRide = useCallback(
    (ride, previousRideDetails?: RideDetails): RideDetails => {
      const getDate = (date: string) => (date ? getDateInTimezone(date) : null);
      const getDateAndAddMinutes = (date: string, minutesToAdd: number) =>
        date ? addMinutes(getDateInTimezone(date), minutesToAdd) : null;

      const updatedAt = ride?.completedAt || ride?.startedAt || ride?.updatedAt;
      const formattedDates = {
        checkedInAt: getDate(ride?.checkedInAt),
        startedAt: getDate(ride?.startedAt),
        completedAt: getDate(ride?.completedAt),
        endsAt: getDate(ride?.endsAt),
        startsAt: getDate(ride?.startsAt),
        // dont format in timezone due to RideOverviewInfo
        // use startedAt/completedAt when available for showing user updates
        updatedAt: updatedAt instanceof Date ? updatedAt.toISOString() : updatedAt,
      };

      const waypoints =
        ride?.waypoints?.map((wp, i) => {
          const rideUpdates = ride?.plannedRoute.legs.find(
            (leg) => leg.endWaypointId === wp.id && leg.liveExpectedEndsAt
          );

          return {
            ...wp,
            id: wp.id,
            actualArrivesAt: getDate(wp.actualArrivesAt),
            actualDepartsAt: getDate(wp.actualDepartsAt),
            // due to the cancelation process, the location might be null
            location: wp.location || previousRideDetails?.waypoints[i]?.location,
            estimatedArrivesAt:
              (wp.actualArrivesAt && getDate(wp.estimatedArrivesAt)) ||
              (rideUpdates?.liveExpectedEndsAt
                ? getDate(rideUpdates?.liveExpectedEndsAt)
                : getDate(wp.estimatedArrivesAt)),
            estimatedDepartsAt:
              (wp.actualDepartsAt && getDate(wp.estimatedDepartsAt)) ||
              getDateAndAddMinutes(rideUpdates?.liveExpectedEndsAt, 5) ||
              (wp.actualArrivesAt && getDateAndAddMinutes(wp.actualArrivesAt, 5)) ||
              getDate(wp.estimatedDepartsAt) ||
              previousRideDetails?.waypoints[i]?.estimatedDepartsAt ||
              null,
          };
        }) || [];

      return {
        ...ride,
        ...formattedDates,
        waypoints,
        ...(ride.tripTemplate && {
          tripTemplate: {
            ...ride.tripTemplate,
            startDate: getDate(ride.tripTemplate.startDate),
            endDate: getDate(ride.tripTemplate.endDate),
          },
        }),
      };
    },
    [getDateInTimezone]
  );

  const {
    data: rideDetails,
    isFetching,
    isLoadingError,
    refetch,
  } = useQuery<RideDetails | undefined>({
    queryKey: [RIDE_QUERY_KEY, cacheKey],
    queryFn: async () => {
      const response = await getRideDetails(rideId);

      if (response?.type === 'success' && response.data?.trip) {
        const previousRideDetails = queryClient.getQueryData<RideDetails>([RIDE_QUERY_KEY, cacheKey]);

        return serializeRide({ ...response.data.trip, slug: rideId }, previousRideDetails);
      }

      throw new Error((response?.type === 'error' && response.error.errors?.[0]) || 'Failed to load ride details');
    },
    enabled: !!rideId,
    refetchOnMount,
  });

  const setRideDetails = useCallback<Dispatch<SetStateAction<RideDetails | undefined>>>(
    (update) => {
      if (update === undefined) {
        // setQueryData treats undefined as a no-op, leaving the previous duplicate in the form.
        void queryClient.resetQueries({ queryKey: [RIDE_QUERY_KEY, cacheKey], exact: true });

        return;
      }

      queryClient.setQueryData<RideDetails | undefined>([RIDE_QUERY_KEY, cacheKey], (current) =>
        typeof update === 'function' ? (update as (current?: RideDetails) => RideDetails | undefined)(current) : update
      );
    },
    [queryClient, cacheKey]
  );

  // _rideSlug is unused: the query is always scoped to the current route's rideId,
  // and every caller today only ever refetches the ride it already has open.
  const fetchRideDetails = useCallback(
    async (_rideSlug?: string) => {
      await refetch();
    },
    [refetch]
  );

  return { rideDetails, setRideDetails, fetchRideDetails, isFetching, isLoadingError, serializeRide };
};
